import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  HiOutlineSparkles,
  HiOutlineTableCells,
  HiOutlineArrowRight,
  HiOutlineArrowRightOnRectangle,
  HiOutlineCheck,
  HiOutlineClipboardDocument,
  HiOutlineChatBubbleLeftRight,
  HiOutlineGlobeAlt,
  HiOutlineMagnifyingGlass,
  HiOutlineShieldCheck,
} from 'react-icons/hi2';
// Same marks the marketing site's tool list uses
// (landing/.../mcp/ScrapeTargets.jsx), so a tool looks the same
// wherever someone meets it.
import {
  FaShopify,
  FaWordpress,
  FaMagento,
  FaSalesforce,
  FaShareAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaPhone,
  FaSitemap,
  FaBroom,
  FaFacebook,
  FaInstagram,
  FaYoutube,
  FaTiktok,
  FaGithub,
  FaPinterest,
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { MdEmail } from 'react-icons/md';
import { IoLayers } from 'react-icons/io5';
import analytics from '../../services/analytics';
import { useAuth } from '../../contexts/AuthContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import { copyText } from '../../utils/clipboard';
import ConnectClaudeModal, { deriveConnectState } from './ConnectClaudeModal';
import { CONNECT_CLIENTS, CONNECT_STORIES, resolveConnectClient, storyParam } from './connectClients';

// Which app "owns" a given chat. On the any-agent story the client carries a
// list of real ones and we rotate through it (index = scenario or card), so the
// illustrations show Claude, Cursor, ChatGPT rather than one generic spark.
const identityAt = (client, index) =>
  client.agents ? client.agents[index % client.agents.length] : { name: client.agentName, Mark: client.Glyph };

// ============================================================================
// Connect — public onboarding / marketing funnel
// ----------------------------------------------------------------------------
// A standalone landing page we funnel Chrome-extension users and marketing
// campaigns to. Anyone can land here and see the marketing story: "scrape the
// web by just asking." The terminal "Get connected" card is state-aware and
// drives the conversion:
//   • signed out          → sign in (inline, in the wizard modal)
//   • signed in, free plan → Pro upsell (routes to /plans)
//   • signed in, paid plan → the connect step for their client
//
// It tells one of three stories, picked from the URL by resolveConnectClient:
//   /connect-claude, /connect?mcp=claude → Claude. Sign-in based, URL-only, no
//                                          MCP jargon and no token anywhere.
//   /connect?mcp=chatgpt                 → ChatGPT. Also sign-in based, via
//                                          developer mode and Plugins.
//   /connect, /connect?mcp               → any other MCP AI agent. Same page,
//                                          same tools, token-based setup.
// Everything that differs between them is a key on the client object (see
// connectClients.jsx) — this file only ever reads `client.*`, never branches on
// which one it is. /connect-claude is the URL shipped extensions link to, so it
// keeps working unchanged.
// ============================================================================

// Scoped keyframes for the chat illustration (mirrors McpView's approach —
// prefixed so the two pages' injected styles never collide).
const connectClaudeStyles = `
@keyframes ccFadeSlideIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes ccFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}
@keyframes ccSpin {
  to { transform: rotate(360deg); }
}
@keyframes ccPopIn {
  0% { opacity: 0; transform: scale(0.8); }
  60% { transform: scale(1.08); }
  100% { opacity: 1; transform: scale(1); }
}
`;

if (typeof document !== 'undefined') {
  const styleId = 'connect-claude-animations';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = connectClaudeStyles;
    document.head.appendChild(style);
  }
}

// ============================================================================
// Content
// ============================================================================

// Copy-ready prompts, grouped by the capability that actually powers them over
// MCP — every one maps to a real tool, so nothing here promises something the
// connector can't do. Deliberately scoped to our strengths (Shopify catalogues,
// JSON-LD product pages, sitemap discovery, contact/lead extraction from URL
// lists, table cleanup & analysis, scheduling) — NOT "scrape any text off any
// page" (that's selector-based and extension-only). `[bracketed]` bits are meant
// to be swapped for the user's own site/input — the "try these on your own data"
// framing.
// Rendered as a Claude thread: each group is a right-aligned user bubble (click
// to copy the prompt verbatim) answered by a short Claude `reply` whose tinted
// tool-chip names the capability doing the work.
const PROMPT_GROUPS = [
  {
    tag: 'Shopify stores',
    Icon: FaShopify,
    tagClass: 'text-shopify-600 dark:text-shopify-400',
    ring: 'border-shopify-400/40 dark:border-shopify-400/30',
    tint: 'bg-shopify-50 dark:bg-shopify-500/10',
    prompt: 'Scrape the entire catalogue of [your-shopify-store.com] into a table — every product and variant with price, SKU and images.',
    reply: 'On it — pulling the full catalogue with the Shopify Extractor.',
  },
  {
    tag: 'Product pages',
    Icon: IoLayers,
    tagClass: 'text-blue-500 dark:text-blue-400',
    ring: 'border-blue-400/40 dark:border-blue-400/30',
    tint: 'bg-blue-50 dark:bg-blue-500/10',
    prompt: 'Pull the name, price, description and SKU from these product URLs: [paste your links].',
    reply: 'Got it — extracting structured product data from those pages.',
  },
  {
    tag: 'Whole-site discovery',
    Icon: HiOutlineGlobeAlt,
    tagClass: 'text-cyan-600 dark:text-cyan-400',
    ring: 'border-cyan-400/40 dark:border-cyan-400/30',
    tint: 'bg-cyan-50 dark:bg-cyan-500/10',
    prompt: 'Map every product URL on [store.com] and scrape them all into one product table.',
    reply: 'Mapping the site first, then scraping every matching page.',
  },
  {
    tag: 'Leads & contacts',
    Icon: MdEmail,
    tagClass: 'text-green-600 dark:text-green-400',
    ring: 'border-green-400/40 dark:border-green-400/30',
    tint: 'bg-green-50 dark:bg-green-500/10',
    prompt: 'Find the contact emails on these sites — deep scan a page deeper too: [paste your links].',
    reply: 'Scanning each landing page (and a layer deeper) for contact emails.',
  },
  {
    tag: 'Clean & analyze',
    Icon: HiOutlineMagnifyingGlass,
    tagClass: 'text-purple-500 dark:text-purple-400',
    ring: 'border-purple-400/40 dark:border-purple-400/30',
    tint: 'bg-purple-50 dark:bg-purple-500/10',
    prompt: 'In my latest scrape, which products are under $[50] and rated 4.5★ or higher?',
    reply: 'Filtering your latest table down to those matches.',
  },
  {
    tag: 'Schedule & export',
    Icon: HiOutlineTableCells,
    tagClass: 'text-rose-600 dark:text-rose-400',
    ring: 'border-rose-400/40 dark:border-rose-400/30',
    tint: 'bg-rose-50 dark:bg-rose-500/10',
    prompt: 'Scrape [store.com] every morning and export the results to a new Google Sheet.',
    reply: "I'll schedule that daily and push results to a Sheet.",
  },
];

// Auto-playing hero conversations — each a real-world job Claude runs through our
// connector, cycling so visitors see the breadth without clicking. The chips name
// the actual tools doing the work (Sitemap Explorer, Shopify Extractor, …), and the
// reply resolves into a real-looking result table — the actual product output, so it
// reads as "clean data you get back", not a placeholder skeleton.
//
// `table` shapes the mini result grid: `cols` are the column headers (last one is
// right-aligned / numeric), and each row has a `name`, `cells` matching the
// middle+trailing columns, and a tile — `img` (a real product photo) for product
// rows, or `mono` (an initial) for rows that stand for a company rather than a
// thing, where a stock photo would be a fiction the eye catches.
const SCENARIOS = [
  {
    prompt: 'Scrape this catalogue: https://shop.example.com/collections/watches',
    chips: [
      { Icon: HiOutlineGlobeAlt, label: 'Sitemap Explorer · mapped 128 product URLs', color: 'text-cyan-500 dark:text-cyan-400' },
      { Icon: IoLayers, label: 'Product details · names, prices, images', color: 'text-blue-500 dark:text-blue-400' },
    ],
    reply: 'Pulled all 128 products into a clean table:',
    meta: '128 rows · 5 columns',
    table: {
      cols: ['Product', 'Price'],
      rows: [
        { img: 'watch-steel-red.webp', name: 'Steel Watch, Red Dial', cells: ['$149.00'] },
        { img: 'watch-gold-green.webp', name: 'Gold Day-Date Watch', cells: ['$129.00'] },
        { img: 'watch-resin-teal.webp', name: 'Digital Sport Watch, Teal', cells: ['$89.00'] },
      ],
    },
  },
  {
    prompt: 'Get me this collection of products: https://shop.example.com/collections/furniture',
    chips: [
      { Icon: FaShopify, label: 'Shopify Extractor · Furniture · 34 products', color: 'text-shopify-500 dark:text-shopify-400' },
    ],
    reply: '34 products — one row per variant, with price & SKU:',
    meta: '34 products · 96 variants',
    table: {
      cols: ['Product', 'SKU', 'Price'],
      rows: [
        { img: 'bed-rattan-arch.webp', name: 'Rattan Arch Bed, Walnut', cells: ['RAT-KNG', '$1,290'] },
        { img: 'bed-curved-headboard.webp', name: 'Curved Headboard Bed', cells: ['CRV-QN', '$980'] },
        { img: 'table-teak-dining.webp', name: 'Solid Wood Dining Table', cells: ['DIN-180', '$1,540'] },
      ],
    },
  },
  {
    prompt: 'Pull the name, description, price and SKU from these 40 product URLs.',
    chips: [
      { Icon: IoLayers, label: 'Product details · 40 pages · 4 fields each', color: 'text-blue-500 dark:text-blue-400' },
    ],
    reply: 'Extracted all 40 products into a clean table:',
    meta: '40 rows · 4 columns',
    table: {
      cols: ['Product', 'SKU', 'Price'],
      rows: [
        { img: 'shoe-white-low.webp', name: 'Low-Top Sneaker, White', cells: ['LOW-WHT', '$120'] },
        { img: 'shoe-red-high.webp', name: 'High-Top Sneaker, Red', cells: ['HGH-RED', '$135'] },
        { img: 'shoe-green-mid.webp', name: 'Turf Cleat, Neon Green', cells: ['TRF-GRN', '$95'] },
      ],
    },
  },
  {
    prompt: 'Find the contact emails on these 150 supplier websites.',
    chips: [
      { Icon: MdEmail, label: 'Email Extractor · scanned 150 sites', color: 'text-green-500 dark:text-green-400' },
    ],
    reply: 'Collected 132 verified emails into a table:',
    meta: '132 emails · 150 sites',
    table: {
      cols: ['Company', 'Contact email'],
      rows: [
        { mono: 'N', tone: 'indigo', name: 'Northwind Textiles', cells: ['ops@northwind.co'] },
        { mono: 'H', tone: 'teal', name: 'Harbor Supply Co.', cells: ['hello@harborsupply.com'] },
        { mono: 'C', tone: 'amber', name: 'Cedar & Co.', cells: ['sales@cedarco.io'] },
      ],
    },
  },
];

// The scraping tools an agent can actually call, by name — mirrored from the
// marketing site's list (landing/.../mcp/ScrapeTargets.jsx), which in
// turn tracks cloud/backend/src/mcp/tools.js. Same order, same names, same
// bodies, same caps; only the colours gain a light-theme half, since this page
// isn't dark-only. Keep the three in sync.

// The platforms social_links detects, in tools.js order. LinkedIn is
// deliberately not shown on any public surface (its name or logo).
const SOCIALS = [
  { Icon: FaXTwitter, name: 'X', color: 'text-gray-700 dark:text-gray-200' },
  { Icon: FaFacebook, name: 'Facebook', color: 'text-[#1877F2]' },
  { Icon: FaInstagram, name: 'Instagram', color: 'text-[#E4405F]' },
  { Icon: FaYoutube, name: 'YouTube', color: 'text-[#FF0000]' },
  { Icon: FaTiktok, name: 'TikTok', color: 'text-gray-800 dark:text-gray-100' },
  { Icon: FaGithub, name: 'GitHub', color: 'text-gray-700 dark:text-gray-200' },
  { Icon: FaPinterest, name: 'Pinterest', color: 'text-[#BD081C]' },
];

const TOOLS = [
  {
    id: 'product-pages',
    Icon: IoLayers,
    iconClass: 'text-blue-500 dark:text-blue-400',
    tint: 'bg-blue-500/10 ring-blue-400/30 dark:ring-blue-400/20',
    name: 'Page Extractor',
    call: 'create_extraction · product_pages',
    body: 'Extract name, price, availability, brand, images and ratings from a list of product URLs. Works on products, articles, job and event pages too.',
    meta: '2,000 pages',
  },
  {
    id: 'shopify',
    Icon: FaShopify,
    iconClass: 'text-[#95BF47]',
    tint: 'bg-[#95BF47]/10 ring-[#95BF47]/30 dark:ring-[#95BF47]/20',
    name: 'Shopify Extractor',
    call: 'extract_shopify_store',
    body: 'Extract an entire Shopify store, or just the collections you name, into one row per variant — price, SKU, options, images, availability.',
    meta: 'Shopify CSV export',
  },
  {
    id: 'woocommerce',
    Icon: FaWordpress,
    iconClass: 'text-purple-500 dark:text-purple-400',
    tint: 'bg-purple-500/10 ring-purple-400/30 dark:ring-purple-400/20',
    name: 'WooCommerce Extractor',
    call: 'extract_catalog',
    body: 'Extract an entire WooCommerce store, or the categories you name, into one row per variant — price, SKU, stock, images, variations.',
    meta: 'Store API feed',
  },
  {
    id: 'magento',
    Icon: FaMagento,
    iconClass: 'text-orange-500 dark:text-orange-400',
    tint: 'bg-orange-500/10 ring-orange-400/30 dark:ring-orange-400/20',
    name: 'Magento Extractor',
    call: 'extract_catalog',
    body: 'Extract an entire Magento store, or the categories you name, into one row per variant — price, SKU, stock, images, configurable options.',
    meta: 'Storefront GraphQL',
  },
  {
    id: 'salesforce-commerce',
    Icon: FaSalesforce,
    iconClass: 'text-[#00A1E0]',
    tint: 'bg-[#00A1E0]/10 ring-[#00A1E0]/30 dark:ring-[#00A1E0]/20',
    name: 'Salesforce Commerce Cloud Extractor',
    call: 'extract_catalog',
    body: 'Extract an entire Salesforce Commerce Cloud store, or the categories you name, into one row per product — price, stock, images and variation options, read from each product’s storefront JSON.',
    meta: 'Storefront controllers',
  },
  {
    id: 'emails',
    Icon: FaEnvelope,
    iconClass: 'text-emerald-500 dark:text-emerald-400',
    tint: 'bg-emerald-500/10 ring-emerald-400/30 dark:ring-emerald-400/20',
    name: 'Email Extractor',
    call: 'create_extraction · emails',
    body: 'Scan landing pages from a URL list for email addresses. Optionally follows internal links a level deeper to reach contact pages.',
    meta: '10,000 sites',
  },
  {
    id: 'social',
    Icon: FaShareAlt,
    iconClass: 'text-sky-500 dark:text-sky-400',
    tint: 'bg-sky-500/10 ring-sky-400/30 dark:ring-sky-400/20',
    name: 'Social Link Scanner',
    call: 'create_extraction · social_links',
    body: 'Scan landing pages from a URL list for links to social profiles, across all eight platforms.',
    socials: true,
    meta: '10,000 sites',
  },
  {
    id: 'maps',
    Icon: FaMapMarkerAlt,
    iconClass: 'text-[#EA4335]',
    tint: 'bg-[#EA4335]/10 ring-[#EA4335]/30 dark:ring-[#EA4335]/20',
    name: 'Map Places Extractor',
    call: 'extract_map_places',
    body: 'Pull name, rating, address, phone, website and opening hours from a list of map place pages.',
    meta: '2,000 listings',
  },
  {
    id: 'phones',
    Icon: FaPhone,
    iconClass: 'text-amber-500 dark:text-amber-400',
    tint: 'bg-amber-500/10 ring-amber-400/30 dark:ring-amber-400/20',
    name: 'Phone Number Extractor',
    call: 'create_extraction · phone_numbers',
    body: 'Scan pages from a URL list for phone numbers, collected beside the page each one came from.',
    meta: '2,000 pages',
  },
  {
    id: 'sitemap',
    Icon: FaSitemap,
    iconClass: 'text-cyan-500 dark:text-cyan-400',
    tint: 'bg-cyan-500/10 ring-cyan-400/30 dark:ring-cyan-400/20',
    name: 'Sitemap Explorer',
    call: 'discover_sitemap',
    body: 'Map every page on a site from its own sitemap, grouped into sections, then pick which ones to scrape. No URL list needed.',
    meta: '25 credits flat',
  },
  {
    id: 'analyze',
    Icon: HiOutlineMagnifyingGlass,
    // The marketing site's brand yellow disappears on this page's light paper,
    // so light gets a darker gold and dark keeps #FFE100.
    iconClass: 'text-yellow-600 dark:text-[#FFE100]',
    tint: 'bg-yellow-400/10 ring-yellow-500/30 dark:bg-[#FFE100]/10 dark:ring-[#FFE100]/20',
    name: 'Website Analyzer',
    call: 'analyze_website',
    body: 'Check any site before you scrape it — detects the platform, finds the sitemap, picks the approach that works.',
    meta: 'Free',
  },
  {
    id: 'cleanup',
    Icon: FaBroom,
    iconClass: 'text-purple-500 dark:text-purple-400',
    tint: 'bg-purple-500/10 ring-purple-400/30 dark:ring-purple-400/20',
    name: 'Data Cleanup',
    call: 'deduplicate_rows · delete_rows · merge_columns',
    // A body can be a function of the client, so the protagonist stays right
    // across the three stories instead of hardcoding "Claude".
    body: (client) =>
      `Let ${client.subject} clean up your data after the scrape — rename or merge columns, drop the ones you don’t need, delete unwanted rows and remove duplicates.`,
    meta: 'No credits',
  },
];

// Step 1 is the only one that genuinely differs (sign-in vs token) — the other
// two just name the protagonist.
const buildHowItWorks = (client) => [
  {
    icon: HiOutlineChatBubbleLeftRight,
    title: client.howStep1.title,
    description: client.howStep1.description,
    circle: 'bg-blue-50 dark:bg-blue-500/15 border-blue-400/30',
    iconColor: 'text-blue-500 dark:text-blue-400',
    glow: 'bg-blue-400/25',
  },
  {
    icon: HiOutlineSparkles,
    title: 'Just ask',
    description: `Tell ${client.subject} what you want in plain English — a site, a category, a list of URLs, contact details, a whole product catalogue.`,
    circle: 'bg-cyan-50 dark:bg-cyan-500/15 border-cyan-400/30',
    iconColor: 'text-cyan-500 dark:text-cyan-400',
    glow: 'bg-cyan-400/25',
  },
  {
    icon: HiOutlineTableCells,
    title: 'Get clean data back',
    description: `${client.Subject} runs the scrape on our cloud browsers and hands back a tidy, queryable table — ready to filter, export, or dig into.`,
    circle: 'bg-purple-50 dark:bg-purple-500/15 border-purple-400/30',
    iconColor: 'text-purple-500 dark:text-purple-400',
    glow: 'bg-purple-400/25',
  },
];

// ============================================================================
// Small building blocks
// ============================================================================

const SectionLabel = ({ children }) => (
  <div className="inline-flex items-center gap-2">
    <span className="w-1.5 h-1.5 rounded-full bg-cyan-500/60 dark:bg-cyan-400/60" />
    <p className="text-xs uppercase tracking-[0.25em] text-cyan-600/80 dark:text-cyan-400/70 font-semibold">
      {children}
    </p>
  </div>
);

// Unified section header — one look and rhythm for every section on the page, so
// the landing scans as a single, grand marketing piece rather than a stack of
// differently-sized blocks.
const SectionHeading = ({ label, title, children }) => (
  <div className="mb-10 sm:mb-16 text-center">
    <div className="flex justify-center">
      <SectionLabel>{label}</SectionLabel>
    </div>
    <h2 className="text-[2rem] sm:text-5xl font-bold text-gray-900 dark:text-white tracking-tight leading-[1.1] mt-4">
      {title}
    </h2>
    {children && (
      <p className="text-base sm:text-lg text-gray-500 dark:text-gray-400 mt-5 max-w-2xl mx-auto leading-relaxed">
        {children}
      </p>
    )}
  </div>
);

// One tool-call row inside a scenario — names the actual tool doing the work and
// reads as a live step: a tinted icon tile, the tool label, then a status that
// starts as a spinner and resolves to a check a beat later (so it looks like work
// happening, not a static pill).
const ScenarioChip = ({ Icon, label, color, delay }) => {
  const [done, setDone] = useState(false);
  useEffect(() => {
    setDone(false);
    // Resolve to "done" shortly after the row appears — the spinner runs in between.
    const t = setTimeout(() => setDone(true), (parseFloat(delay) + 0.9) * 1000);
    return () => clearTimeout(t);
  }, [delay, label]);

  return (
    <div style={{ animation: `ccFadeSlideIn 0.45s ease-out ${delay} both` }}>
      <div className="inline-flex items-center gap-2.5 pl-2 pr-3 py-2 rounded-xl bg-[#00000005] dark:bg-white/[0.04] border border-[#0000000d] dark:border-white/[0.06] min-w-0 max-w-full">
        <span className={`flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center bg-white/70 dark:bg-white/[0.06] border border-[#0000000a] dark:border-white/[0.06] ${color}`}>
          <Icon className="w-3.5 h-3.5" />
        </span>
        <span className="min-w-0 truncate text-[13px] font-medium text-[#5a5952] dark:text-gray-300">{label}</span>
        {done ? (
          <span
            className="flex-shrink-0 w-[18px] h-[18px] rounded-full bg-emerald-500 flex items-center justify-center"
            style={{ animation: 'ccPopIn 0.3s ease-out both' }}
          >
            <HiOutlineCheck className="w-3 h-3 text-white" strokeWidth={3} />
          </span>
        ) : (
          // --cc-accent is set on the page root, so the spinner picks up whichever
          // story is being told without threading a color prop down here.
          <span
            className="flex-shrink-0 w-4 h-4 rounded-full border-[1.5px] border-gray-300 dark:border-gray-600"
            style={{ animation: 'ccSpin 0.7s linear infinite', borderTopColor: 'var(--cc-accent)' }}
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  );
};

// A colour per company, so a column of monograms reads like a column of real
// favicons rather than three copies of one grey chip.
const MONO_TONES = {
  indigo: 'bg-indigo-50 text-indigo-500 ring-indigo-400/30 dark:bg-indigo-400/15 dark:text-indigo-300 dark:ring-indigo-400/25',
  teal: 'bg-teal-50 text-teal-600 ring-teal-400/30 dark:bg-teal-400/15 dark:text-teal-300 dark:ring-teal-400/25',
  amber: 'bg-amber-50 text-amber-600 ring-amber-400/30 dark:bg-amber-400/15 dark:text-amber-300 dark:ring-amber-400/25',
};

// A single result row in the mini table — a product photo (or a company's
// initial), the name, then the trailing cells (SKU, price, email…). The last
// column is the emphasised value, right-aligned with tabular figures.
const ResultRow = ({ row, cols, delay }) => (
  <div
    className="flex items-center gap-3 px-3 py-2.5"
    style={{ animation: `ccFadeSlideIn 0.4s ease-out ${delay}s both` }}
  >
    {row.img ? (
      // The photos are shot on a near-white ground, so on the light theme's warm
      // paper the ring is the only thing holding the tile's edge — keep it darker
      // here than the 4% the gradient swatch needed.
      <img
        src={`/assets/demo-products/${row.img}`}
        alt=""
        width={28}
        height={28}
        className="flex-shrink-0 w-7 h-7 rounded-lg object-cover shadow-sm ring-1 ring-black/[0.08] dark:ring-white/[0.06]"
      />
    ) : (
      <span
        className={`flex-shrink-0 w-7 h-7 rounded-lg ring-1 flex items-center justify-center text-[11px] font-semibold ${MONO_TONES[row.tone] ?? MONO_TONES.indigo}`}
        aria-hidden="true"
      >
        {row.mono}
      </span>
    )}
    <span className="flex-1 min-w-0 truncate text-sm font-medium text-[#3d3d3a] dark:text-gray-200">{row.name}</span>
    {row.cells.map((cell, i) => {
      const last = i === row.cells.length - 1;
      return (
        <span
          key={i}
          className={`flex-shrink-0 tabular-nums ${
            last
              ? 'text-sm font-semibold text-[#2d2d2a] dark:text-gray-100'
              : 'hidden sm:inline text-[11px] font-mono text-[#8a8980] dark:text-gray-500'
          }`}
        >
          {cell}
        </span>
      );
    })}
  </div>
);

// ============================================================================
// Hero illustration — an agent chat that auto-plays through real jobs. The
// surface is the assistant (people work inside their own chat); our connector is
// the tool it calls. Cycles through SCENARIOS so visitors see the breadth
// without clicking; pauses on hover and respects prefers-reduced-motion.
// The mark and name come from the client, so the same canvas serves both
// stories without pretending a generic MCP agent is Claude.
// ============================================================================

const AutoChat = ({ client }) => {
  const [index, setIndex] = useState(0);

  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Deliberately no pause-on-hover: the chat sits beside the hero copy, so a
  // pointer resting there while reading would silently stop the cycle. The dots
  // below are the way to steer it.
  useEffect(() => {
    if (reduceMotion) return undefined;
    const t = setTimeout(() => setIndex((i) => (i + 1) % SCENARIOS.length), 5200);
    return () => clearTimeout(t);
  }, [index, reduceMotion]);

  const scenario = SCENARIOS[index];
  const { name: agentName, Mark: Glyph } = identityAt(client, index);
  // Tool chips stagger in after the prompt; the reply lands after the chips.
  const chipBase = 0.7;
  const replyDelay = chipBase + scenario.chips.length * 0.55 + 0.35;

  return (
    <div className="relative w-full max-w-2xl lg:max-w-none mx-auto" style={{ animation: 'ccFloat 7s ease-in-out infinite' }}>
      {/* Soft warm ambient shadow — Claude's canvas is calm; no multicolor glow */}
      <div
        className="absolute -inset-4 bg-black/[0.04] dark:bg-black/40 rounded-[2rem] blur-3xl"
        aria-hidden="true"
      />
      {/* The Claude.ai canvas — warm paper (light) / flat warm-dark (dark), soft
          rounded, minimal chrome. This IS the chat surface, not an app window. */}
      <div className="relative rounded-[1.75rem] border border-[#00000010] dark:border-white/[0.06] bg-[#faf9f5] dark:bg-[#262624] shadow-xl shadow-[#00000012] dark:shadow-black/40 overflow-hidden">
        {/* Header — just Claude's mark + name sitting on the canvas, and a quiet
            "connected" pill. No divider bar; Claude.ai keeps the top airy. */}
        <div className="flex items-center gap-2.5 px-6 pt-5 pb-2">
          <Glyph className="w-6 h-6" text="text-[13px]" />
          <span className="text-[15px] font-medium text-[#3d3d3a] dark:text-gray-200 tracking-tight">{agentName}</span>
          <span className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#00000006] dark:bg-white/[0.04] flex-shrink-0">
            <span className="w-4 h-4 rounded-[4px] overflow-hidden flex-shrink-0">
              <img src="/logo-128.png" alt="Ultimate Web Scraper" className="w-full h-full" />
            </span>
            <span className="text-[10px] font-medium text-[#73726c] dark:text-gray-400 truncate max-w-[104px]">Ultimate Web Scraper</span>
            <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
            </span>
          </span>
        </div>

        {/* Conversation — keyed by scenario so the reveal animation replays each cycle.
            Styled like a real Claude thread: right-aligned user message in a soft neutral
            bubble, left-aligned assistant turn led by the Claude mark (no bubble). */}
        <div key={index} className="px-6 py-4 space-y-5 min-h-[300px]">
          {/* User turn — right-aligned, in Claude's warm input bubble (a touch
              darker than the canvas), no border. */}
          <div className="flex justify-end" style={{ animation: 'ccFadeSlideIn 0.5s ease-out 0.15s both' }}>
            <div className="max-w-[85%] px-4 py-2.5 rounded-[1.25rem] rounded-br-lg bg-[#00000008] dark:bg-white/[0.05] text-[#3d3d3a] dark:text-gray-100 text-[15px] leading-relaxed break-words">
              {scenario.prompt}
            </div>
          </div>

          {/* Assistant turn — Claude glyph, then the tool steps + reply flush
              against it, sitting directly on the canvas (no bubble). */}
          <div className="flex gap-3.5">
            <Glyph className="w-6 h-6 mt-0.5" text="text-[13px]" />
            <div className="flex-1 min-w-0 space-y-3">
              {scenario.chips.map((chip, i) => (
                <ScenarioChip
                  key={i}
                  Icon={chip.Icon}
                  label={chip.label}
                  color={chip.color}
                  delay={`${chipBase + i * 0.55}s`}
                />
              ))}

              <div className="space-y-2.5" style={{ animation: `ccFadeSlideIn 0.5s ease-out ${replyDelay}s both` }}>
                <p className="text-[15px] text-[#3d3d3a] dark:text-gray-200 leading-relaxed">{scenario.reply}</p>

                {/* Result table — a quiet card on the warm canvas */}
                <div className="rounded-xl border border-[#0000000d] dark:border-white/[0.07] bg-white/60 dark:bg-white/[0.02] overflow-hidden">
                  {/* Column headers */}
                  <div className="flex items-center gap-3 px-3 py-2 bg-[#00000004] dark:bg-white/[0.03] border-b border-[#0000000a] dark:border-white/[0.05]">
                    <span className="w-7 flex-shrink-0" aria-hidden="true" />
                    {scenario.table.cols.map((col, i) => {
                      const last = i === scenario.table.cols.length - 1;
                      const mid = i > 0 && !last;
                      return (
                        <span
                          key={col}
                          className={`text-[10px] font-semibold uppercase tracking-wider text-[#8a8980] dark:text-gray-500 ${
                            i === 0 ? 'flex-1' : mid ? 'hidden sm:inline flex-shrink-0' : 'flex-shrink-0'
                          }`}
                        >
                          {col}
                        </span>
                      );
                    })}
                  </div>
                  {/* Rows */}
                  <div className="divide-y divide-[#0000000a] dark:divide-white/[0.04]">
                    {scenario.table.rows.map((row, i) => (
                      <ResultRow
                        key={i}
                        row={row}
                        cols={scenario.table.cols}
                        delay={replyDelay + 0.2 + i * 0.12}
                      />
                    ))}
                  </div>
                  {/* Footer meta — grounds it as a real, exported table */}
                  <div
                    className="flex items-center gap-2 px-3 py-2 bg-[#00000004] dark:bg-white/[0.03] border-t border-[#0000000a] dark:border-white/[0.05]"
                    style={{ animation: `ccFadeSlideIn 0.4s ease-out ${replyDelay + 0.6}s both` }}
                  >
                    <HiOutlineTableCells className="w-3.5 h-3.5 text-[#8a8980] dark:text-gray-500 flex-shrink-0" />
                    <span className="text-[11px] text-[#8a8980] dark:text-gray-500">{scenario.meta}</span>
                    <span className="ml-auto text-[11px] font-medium" style={{ color: 'var(--cc-accent)' }}>View table →</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scenario dots — indicate/jump between the playing prompts */}
        <div className="flex items-center justify-center gap-1.5 pb-4">
          {SCENARIOS.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`Show example ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === index ? 'w-5' : 'w-1.5 bg-[#00000015] dark:bg-gray-600 hover:bg-[#00000025] dark:hover:bg-gray-500'
              }`}
              style={i === index ? { backgroundColor: 'var(--cc-accent)' } : undefined}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Example prompts — each capability is its own little chat: a warm canvas card
// (same surface as the hero) with the agent's header, a right-aligned "You"
// bubble you'd send (the whole bubble copies the prompt), and a short reply
// whose tinted tool-chip names the capability. Six cards tile on a grid.
// ============================================================================

// Split a prompt into text + [bracketed] token spans so the swap-me bits glow in
// the story's accent, signalling "replace this with your own store / URLs".
const renderPrompt = (text) =>
  text.split(/(\[[^\]]+\])/g).map((part, i) =>
    part.startsWith('[') && part.endsWith(']') ? (
      <span key={i} className="font-medium" style={{ color: 'var(--cc-accent)' }}>{part}</span>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );

// One self-contained mini chat card: agent header, a "You" bubble (the whole
// bubble copies the prompt verbatim), and a reply stub whose tinted tool-chip
// keeps the capability signal.
const PromptChat = ({ group, client, index = 0 }) => {
  const { Icon } = group;
  const { name: agentName, Mark: Glyph } = identityAt(client, index);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    copyText(group.prompt).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="flex flex-col rounded-[1.5rem] border border-[#00000010] dark:border-white/[0.06] bg-[#faf9f5] dark:bg-[#262624] shadow-lg shadow-[#0000000f] dark:shadow-black/30 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 px-4 sm:px-5 py-4 sm:py-5">
      {/* Card header — the agent's glyph + name, then the capability it maps to */}
      <div className="flex items-center gap-2.5 pb-3 mb-3 border-b border-[#0000000a] dark:border-white/[0.05]">
        <Glyph className="w-5 h-5" />
        <span className="text-[13px] font-medium text-[#3d3d3a] dark:text-gray-200 tracking-tight">{agentName}</span>
        <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-medium text-[#8a8980] dark:text-gray-500">
          <Icon className={`w-3.5 h-3.5 ${group.tagClass}`} />
          {group.tag}
        </span>
      </div>

      {/* You — right-aligned bubble; the whole thing is the copy button */}
      <div className="flex justify-end">
        <button
          onClick={handleCopy}
          title="Click to copy"
          className="group/b relative max-w-[92%] text-left rounded-[1.25rem] rounded-br-lg bg-[#00000008] dark:bg-white/[0.05] hover:bg-[#0000000d] dark:hover:bg-white/[0.08] px-4 py-3 transition-colors"
        >
          <span className="block text-[15px] leading-relaxed text-[#3d3d3a] dark:text-gray-100 break-words">
            {renderPrompt(group.prompt)}
          </span>
          {/* Copy hint stays visible (quiet) so touch users see it, flips to a check on copy */}
          <span
            className={`mt-2 flex items-center gap-1.5 text-[11px] font-medium transition-colors ${
              copied ? 'text-emerald-500' : 'text-[#8a8980] dark:text-gray-500 opacity-60 group-hover/b:opacity-100'
            }`}
          >
            {copied ? (
              <><HiOutlineCheck className="w-3.5 h-3.5" /> Copied</>
            ) : (
              <><HiOutlineClipboardDocument className="w-3.5 h-3.5" /> Click to copy</>
            )}
          </span>
        </button>
      </div>

      {/* Reply stub — the glyph + a tinted tool chip that keeps the capability
          signal, so it reads as "the agent runs the tool and answers". */}
      <div className="flex items-start gap-2.5 mt-3">
        <Glyph className="w-5 h-5 mt-0.5" />
        <span className={`inline-flex items-start gap-2 rounded-2xl border ${group.ring} ${group.tint} pl-2.5 pr-3 py-1.5`}>
          <Icon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${group.tagClass}`} />
          <span className="text-[13px] leading-snug text-[#3d3d3a] dark:text-gray-300 break-words">{group.reply}</span>
        </span>
      </div>
    </div>
  );
};

// ============================================================================
// Tools showcase — the real Ultimate Web Scraper tools, wired into the agent.
// Shows the agent as a "hub" and, connected below it, the actual tools (same
// marks & colors as the extension) so the value reads as concrete and familiar,
// not generic.
// ============================================================================

// Same card as the marketing site's tool list: tinted mark and a cap pill on one
// line, then the tool's name, the call the agent actually makes, and what it
// gets you. Naming the call is the point — it's why the list reads as a real
// toolkit rather than a feature grid.
const ToolCard = ({ tool, client }) => {
  const { Icon } = tool;
  const body = typeof tool.body === 'function' ? tool.body(client) : tool.body;
  return (
    <div className="flex flex-col rounded-2xl border border-gray-200/60 dark:border-white/10 bg-white/70 dark:bg-white/[0.03] backdrop-blur-xl p-5 shadow-sm hover:shadow-lg hover:border-gray-300/80 dark:hover:border-white/20 hover:-translate-y-0.5 transition-all duration-300">
      <div className="flex items-start justify-between gap-3">
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ring-1 ring-inset ${tool.tint}`}>
          <Icon className={`h-5 w-5 ${tool.iconClass}`} aria-hidden="true" />
        </span>
        <span className="shrink-0 rounded-full border border-gray-200/70 dark:border-white/10 bg-gray-50/80 dark:bg-white/[0.03] px-2.5 py-1 text-[10px] font-medium text-gray-500 dark:text-gray-500">
          {tool.meta}
        </span>
      </div>

      <h3 className="mt-4 text-base font-bold text-gray-800 dark:text-gray-100">{tool.name}</h3>
      <code className="mt-1 block font-mono text-[11px] text-gray-400 dark:text-gray-600">{tool.call}</code>
      <p className="mt-2.5 text-sm leading-relaxed text-gray-500 dark:text-gray-400">{body}</p>

      {tool.socials && (
        <div className="mt-3.5 flex flex-wrap items-center gap-1.5">
          {SOCIALS.map((s) => (
            <span
              key={s.name}
              className={`flex h-7 w-7 items-center justify-center rounded-lg bg-gray-50/80 dark:bg-white/[0.04] ring-1 ring-inset ring-black/[0.06] dark:ring-white/10 ${s.color}`}
            >
              <s.Icon className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="sr-only">{s.name}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const ToolsShowcase = ({ client }) => (
  <div className="flex flex-col items-center">
    {/* The agent hub — everything below plugs into it */}
    <div className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-white/80 dark:bg-gray-900/60 backdrop-blur-xl border border-gray-200/70 dark:border-white/[0.08] shadow-sm">
      {/* The any-agent hub shows the real marks overlapping — the toolkit below
          plugs into whichever of them you use, and a stack says that faster
          than a placeholder icon. */}
      {client.agentTiles ? (
        <span className="flex -space-x-1">
          {client.agentTiles.map(({ name, Mark }) => (
            <Mark key={name} className="w-7 h-7 ring-2 ring-white dark:ring-gray-900 rounded-md" />
          ))}
        </span>
      ) : (
        <client.HubMark className="w-7 h-7" />
      )}
      <span className="text-base font-semibold text-gray-800 dark:text-gray-100">{client.agentName}</span>
      <span className="inline-flex items-center gap-1.5 pl-3 ml-0.5 border-l border-gray-200 dark:border-white/10">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
        </span>
        <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">connected</span>
      </span>
    </div>

    {/* Connector down into the toolkit */}
    <div className="w-px h-8 bg-gradient-to-b from-cyan-400/70 to-cyan-400/0" aria-hidden="true" />
    <p className="text-sm text-gray-400 dark:text-gray-500 mb-7">{client.hubSub}</p>

    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 w-full max-w-6xl">
      {TOOLS.map((tool) => (
        <ToolCard key={tool.id} tool={tool} client={client} />
      ))}
    </div>

    {/* The tail of the toolkit — querying, exports, schedules and docs answers
        don't earn a card each, but they're the difference between a scrape and
        a finished job. Same line the marketing site closes its tool list with. */}
    <p className="mt-8 text-center text-sm text-gray-400 dark:text-gray-500 max-w-2xl mx-auto leading-relaxed">
      Plus {client.subject} queries and exports the tables that come back — to CSV, Excel or Google
      Sheets — keeps any job on a daily schedule, and answers “how do I…?” straight from our docs.
    </p>
  </div>
);

// A segmented control, not navigation: the page underneath is the same, only
// the protagonist changes. Lives in the header so someone who landed on the
// wrong story can flip without hunting for a URL — the sign-in pages' only other
// exit is a line of small print at the very bottom. Three tabs share a phone
// header with the logo and sign-in, hence the tighter padding below xs.
const StorySwitcher = ({ current, onSelect }) => (
  <div
    className="inline-flex items-center rounded-full border border-gray-200/70 dark:border-white/[0.08] bg-white/70 dark:bg-white/[0.04] p-0.5 flex-shrink-0"
    role="tablist"
    aria-label="Which AI app are you connecting?"
  >
    {CONNECT_STORIES.map(({ key, switcherLabel }) => {
      const selected = key === current;
      return (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={selected}
          onClick={() => onSelect(key)}
          className={`px-2.5 sm:px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-semibold whitespace-nowrap transition-all duration-200 ${
            selected
              ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-sm shadow-cyan-500/25'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
          }`}
        >
          {switcherLabel}
        </button>
      );
    })}
  </div>
);

// ============================================================================
// Main page
// ============================================================================

const ConnectClaude = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, subscription, subscriptionLoading } = useAuth();

  // Which story this URL is telling. Everything below reads `client.*`.
  const client = resolveConnectClient(location.pathname, searchParams);
  usePageTitle(client.pageTitle);

  const howItWorks = buildHowItWorks(client);
  const viewedRef = useRef(false);

  // Connecting requires a paid plan — the backend gates it with 402
  // PLAN_REQUIRED. Any non-free plan is "Pro" for this funnel's purposes,
  // matching McpView / the OAuth consent gate.
  const planName = subscription?.subscription?.plan?.name || 'free';
  const isPaidPlan = planName !== 'free';

  // The connect state we tag analytics with — the same helper the modal uses
  // internally to pick its step, so the label can't drift from the rendered UI.
  const panelState = deriveConnectState({ isAuthenticated, subscriptionLoading, isPaidPlan });

  // ?setup=1 opens the focused Connect-Claude wizard — a modal over this page.
  // Sign-in is inline in the modal, so finishing it just re-derives the step in
  // place — no redirect, no landing back at the page bottom.
  const setupMode = searchParams.get('setup') === '1';

  // Fire the view event once, after we know enough to tag the plan.
  useEffect(() => {
    if (viewedRef.current) return;
    if (isAuthenticated && subscriptionLoading) return;
    viewedRef.current = true;
    analytics.capture(analytics.EVENTS.CONNECT_CLAUDE_VIEWED, {
      authenticated: isAuthenticated,
      plan: isAuthenticated ? planName : undefined,
      state: panelState,
      mode: setupMode ? 'wizard' : 'landing',
      client: client.key,
    });
  }, [isAuthenticated, subscriptionLoading, planName, panelState, setupMode, client.key]);

  // Launch the focused setup wizard, preserving any utm_*/campaign params — and
  // staying on whichever route we were served from, so /connect-claude visitors
  // don't get bounced onto /connect mid-flow.
  const goToSetup = useCallback(() => {
    analytics.capture(analytics.EVENTS.CONNECT_CLAUDE_CTA_CLICKED, {
      authenticated: isAuthenticated,
      plan: isAuthenticated ? planName : undefined,
      state: panelState,
      client: client.key,
    });
    const params = new URLSearchParams(searchParams);
    params.set('setup', '1');
    navigate(`${location.pathname}?${params.toString()}`);
  }, [isAuthenticated, planName, panelState, client.key, navigate, searchParams, location.pathname]);

  const exitSetup = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.delete('setup');
    const qs = params.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ''}`);
  }, [navigate, searchParams, location.pathname]);

  // Swap stories, keeping any utm_*/campaign params. Always lands on /connect —
  // /connect-claude ignores the param by design, so it can't express any other
  // story. `keepSetup` is for the in-wizard hand-off ("they sign in instead"),
  // which should reopen the wizard rather than dump them back on the landing
  // page; the header switcher closes it, since that's a browse action, not a step.
  const switchClient = useCallback(
    (key, { keepSetup = false } = {}) => {
      const params = new URLSearchParams(searchParams);
      params.set('mcp', storyParam(key));
      if (keepSetup) params.set('setup', '1');
      else params.delete('setup');
      navigate(`/connect?${params.toString()}`);
    },
    [navigate, searchParams]
  );

  // From the token wizard: "Claude and ChatGPT sign in instead" — swap to that
  // story with the wizard still open, so they don't lose their place.
  const switchStoryInWizard = useCallback(
    (key) => switchClient(key, { keepSetup: true }),
    [switchClient]
  );

  return (
    // --cc-accent is the story's chat accent (Claude coral vs our cyan). Set once
    // here so the illustrations can reach it without prop-drilling a color.
    <div
      className="min-h-screen relative overflow-x-clip bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 dark:from-black dark:via-gray-950 dark:to-black text-gray-900 dark:text-white"
      style={{ '--cc-accent': client.accent }}
    >
      {/* Ambient background glows */}
      <div className="absolute -left-24 -top-24 w-80 h-80 sm:w-96 sm:h-96 bg-blue-500/5 dark:bg-blue-500/10 blur-3xl rounded-full animate-pulse pointer-events-none" aria-hidden="true" style={{ animationDuration: '8s' }} />
      <div className="absolute -right-24 top-64 w-80 h-80 sm:w-96 sm:h-96 bg-purple-500/5 dark:bg-purple-500/10 blur-3xl rounded-full animate-pulse pointer-events-none" aria-hidden="true" style={{ animationDuration: '10s', animationDelay: '1s' }} />

      {/* Top nav — brand lockup, quiet in-page anchors (hidden on phones), sign-in/CTA.
          Sticky + backdrop-blur so it reads as finished chrome, not a floating line. */}
      <header className="sticky top-0 z-30 border-b border-gray-200/50 dark:border-white/[0.06] bg-gray-50/70 dark:bg-black/50 backdrop-blur-xl supports-[backdrop-filter]:bg-gray-50/60 dark:supports-[backdrop-filter]:bg-black/40">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-16 py-4 sm:py-5 flex items-center justify-between gap-4 lg:grid lg:grid-cols-[1fr_auto_1fr]">
          <a href="#top" className="flex items-center gap-2.5 flex-shrink-0 group">
            <div className="w-8 h-8 rounded-lg p-[1.5px] bg-gradient-to-br from-blue-400 via-cyan-400 to-purple-400 shadow-md shadow-cyan-500/20">
              <div className="w-full h-full bg-white rounded-[7px] p-1 flex items-center justify-center">
                <img src="/logo-128.png" alt="Ultimate Web Scraper logo" className="w-full h-full" />
              </div>
            </div>
            {/* Wordmark yields below sm so the three-tab story switcher and
                sign-in still fit; the logo alone carries the brand there. Both
                header children are flex-shrink-0 under an overflow-x-clip root,
                so anything that doesn't fit is clipped away silently — the
                wordmark has to yield for the whole band where all three tabs
                are at full size. */}
            <span className="hidden sm:inline text-[10px] sm:text-xs uppercase tracking-[0.25em] font-semibold text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors">
              Ultimate Web Scraper
            </span>
          </a>

          {/* Quiet in-page nav — hidden below lg (brand + sign-in stay), matches the page's lg: cutover.
              On lg it sits in the centered grid cell so the links center on the page, not the leftover space. */}
          <nav className="hidden lg:flex items-center justify-center gap-1">
            {[
              { href: '#tools', label: 'Tools' },
              { href: '#examples', label: 'Examples' },
              { href: '#how-it-works', label: 'How it works' },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="px-3.5 py-2 rounded-lg text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100/70 dark:hover:bg-white/[0.04] transition-all duration-200"
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Switcher rides with sign-in rather than taking a fourth grid cell,
              so the header keeps its brand / nav / actions rhythm. */}
          <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0 lg:justify-self-end">
            <StorySwitcher current={client.key} onSelect={switchClient} />
            <button
              onClick={() => navigate(isAuthenticated ? '/dashboard' : '/signin')}
              className="flex-shrink-0 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors -my-2 py-2 whitespace-nowrap"
            >
              {isAuthenticated ? 'Go to dashboard →' : 'Sign in'}
            </button>
          </div>
        </div>
      </header>

      <div id="top" className="relative max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-16 pb-20">
        {/* ---------------------------------------------------------------- */}
        {/* Hero */}
        {/* ---------------------------------------------------------------- */}
        <section className="grid lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] gap-12 lg:gap-16 items-center pt-6 sm:pt-14 lg:pt-20 pb-14 sm:pb-28">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-1.5 rounded-full bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-cyan-500/30 dark:border-cyan-400/30 text-cyan-600 dark:text-cyan-300 mb-5 sm:mb-7">
              <HiOutlineSparkles className="w-4 h-4" />
              {client.heroBadge}
            </span>
            <h1 className="text-[2.5rem] sm:text-6xl lg:text-7xl font-bold text-gray-900 dark:text-white tracking-tight leading-[1.05] mb-7 text-balance">
              {client.heroTitleLead}{' '}
              <span className="bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 dark:from-blue-400 dark:via-cyan-400 dark:to-purple-400 bg-clip-text text-transparent">
                web scraping powers
              </span>
            </h1>
            <p className="text-base sm:text-xl text-gray-600 dark:text-gray-300 leading-relaxed mb-8 sm:mb-10">
              {client.heroSub}
            </p>

            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <button
                onClick={goToSetup}
                className="inline-flex items-center justify-center gap-2.5 px-8 py-4 text-lg font-bold text-white rounded-2xl bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 hover:from-blue-400 hover:via-cyan-400 hover:to-purple-400 shadow-xl shadow-cyan-500/25 hover:shadow-2xl hover:shadow-cyan-500/35 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 w-full sm:w-auto"
              >
                <HiOutlineChatBubbleLeftRight className="w-5 h-5" />
                {client.ctaLabel}
              </button>
              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 text-base font-semibold text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white rounded-2xl hover:bg-gray-100/70 dark:hover:bg-white/[0.04] transition-all duration-200 w-full sm:w-auto"
              >
                See how it works
                <HiOutlineArrowRight className="w-4 h-4" />
              </a>
            </div>

            <div className={`flex flex-wrap items-center gap-2.5 mt-8 sm:mt-10 ${client.worksWith ? '' : 'hidden'}`}>
              <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-gray-400 dark:text-gray-500 mr-1">Works with</span>
              {(client.worksWith || []).map(({ label, Mark }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100/80 dark:bg-white/[0.04] border border-gray-200/60 dark:border-white/[0.06]"
                >
                  <Mark />
                  {label}
                </span>
              ))}
            </div>
          </div>

          <AutoChat client={client} />
        </section>

        {/* App strip — the compatibility claim at a glance, in the apps' own
            marks. Only the any-agent story has one; /connect-claude commits to
            Claude and a row of competitors there would muddle it. */}
        {client.appStrip && (
          <div className="-mt-6 sm:-mt-16 mb-14 sm:mb-24 text-center">
            <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-gray-400 dark:text-gray-500">
              Works with MCP-compatible AI apps
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-7 gap-y-3">
              {client.appStrip.map(({ name, Mark }) => (
                <span key={name} className="flex items-center gap-2.5 opacity-70 hover:opacity-100 transition-opacity">
                  <Mark className="w-7 h-7" />
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{name}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Tools showcase — our real toolkit, wired into Claude */}
        {/* ---------------------------------------------------------------- */}
        <section id="tools" className="scroll-mt-24 mb-16 sm:mb-32">
          <SectionHeading label={client.toolsLabel} title="The tools that do the scraping">
            {client.toolsSub}
          </SectionHeading>
          <ToolsShowcase client={client} />
        </section>

        {/* ---------------------------------------------------------------- */}
        {/* Prompt examples — a Claude thread of copyable asks */}
        {/* ---------------------------------------------------------------- */}
        <section id="examples" className="scroll-mt-24 mb-16 sm:mb-32">
          <SectionHeading label="Just say the word" title="Get started with these">
            {client.examplesSubLead}{' '}
            <span className="font-semibold" style={{ color: 'var(--cc-accent)' }}>[bracketed]</span>{' '}
            bits for your own store, URLs or input, and paste it in.
          </SectionHeading>

          {/* Each capability is its own mini chat card, tiled on a grid */}
          <div className="grid gap-5 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
            {PROMPT_GROUPS.map((group, i) => (
              <PromptChat key={group.tag} group={group} client={client} index={i} />
            ))}
          </div>
        </section>

        {/* ---------------------------------------------------------------- */}
        {/* How it works */}
        {/* ---------------------------------------------------------------- */}
        <section id="how-it-works" className="scroll-mt-24 mb-16 sm:mb-32">
          <SectionHeading label="How it works" title="Three steps to data on tap" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-6xl mx-auto">
            {howItWorks.map(({ icon: Icon, title, description, circle, iconColor, glow }, i) => (
              <div
                key={title}
                className="group relative rounded-2xl p-7 sm:p-8 bg-white/70 dark:bg-gray-900/40 backdrop-blur-xl border border-gray-200/60 dark:border-gray-700/40 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
              >
                <span className="absolute top-6 right-6 text-5xl font-bold text-gray-100 dark:text-white/[0.04] select-none tabular-nums">
                  {i + 1}
                </span>
                <div className="relative w-14 h-14 mb-5">
                  <div className={`absolute inset-0 ${glow} blur-md rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                  <div className={`relative w-14 h-14 rounded-full border flex items-center justify-center ${circle}`}>
                    <Icon className={`w-6 h-6 ${iconColor}`} />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-2">{title}</h3>
                <p className="text-base text-gray-500 dark:text-gray-400 leading-relaxed">{description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ---------------------------------------------------------------- */}
        {/* Get connected — a clean 3-step preview that launches the wizard */}
        {/* ---------------------------------------------------------------- */}
        <section id="connect" className="scroll-mt-24">
          {/* Closing CTA — one grand panel that pulls the whole page to a single action */}
          <div className="relative max-w-5xl mx-auto rounded-[2rem] overflow-hidden border border-gray-200/70 dark:border-white/[0.08] bg-white/70 dark:bg-gray-900/40 backdrop-blur-xl shadow-xl">
            {/* Warm/brand wash so the closing panel feels like a destination */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/[0.06] via-cyan-500/[0.05] to-purple-500/[0.06] pointer-events-none" aria-hidden="true" />
            <div className="relative px-6 sm:px-12 py-10 sm:py-16 text-center">
              <div className="flex justify-center">
                <SectionLabel>Get connected</SectionLabel>
              </div>
              <h2 className="text-[2rem] sm:text-5xl font-bold text-gray-900 dark:text-white tracking-tight leading-[1.1] mt-4">
                Ready when you are
              </h2>
              <p className="text-base sm:text-lg text-gray-500 dark:text-gray-400 mt-5 max-w-xl mx-auto leading-relaxed">
                {client.closingSub}
              </p>

              <div className="max-w-2xl mx-auto grid sm:grid-cols-2 gap-4 mt-10 mb-10 text-left">
                {[
                  { n: 1, icon: HiOutlineArrowRightOnRectangle, title: 'Sign in', desc: 'Sign in or create a free account — right here, no leaving the page.' },
                  { n: 2, icon: HiOutlineChatBubbleLeftRight, ...client.closingStep2 },
                ].map(({ n, icon: Icon, title, desc }) => (
                  <div key={n} className="relative rounded-2xl p-6 bg-white/80 dark:bg-white/[0.03] border border-gray-200/60 dark:border-white/[0.06] shadow-sm">
                    <div className="flex items-center gap-3 mb-2.5">
                      <span className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 shadow-sm shadow-cyan-500/30">{n}</span>
                      <Icon className="w-5 h-5 text-cyan-500 dark:text-cyan-400" />
                      <h3 className="text-base font-bold text-gray-800 dark:text-gray-100">{title}</h3>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>

              <button
                onClick={goToSetup}
                className="inline-flex items-center justify-center gap-2.5 px-9 py-4 text-lg font-bold text-white rounded-2xl bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 hover:from-blue-400 hover:via-cyan-400 hover:to-purple-400 shadow-xl shadow-cyan-500/25 hover:shadow-2xl hover:shadow-cyan-500/35 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 w-full sm:w-auto"
              >
                <HiOutlineChatBubbleLeftRight className="w-5 h-5" />
                {client.ctaLabel}
                <HiOutlineArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Security reassurance */}
          <div className="max-w-2xl mx-auto mt-8 flex gap-3.5 px-5 py-4 rounded-xl bg-gray-50/80 dark:bg-gray-800/40 border border-gray-200/60 dark:border-gray-700/40">
            <HiOutlineShieldCheck className="w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
              {client.securityNote}
            </p>
          </div>
        </section>
      </div>

      {/* The setup wizard, as a modal over this page. ?setup=1 (set by the CTAs)
          opens it; closing clears the param. */}
      <ConnectClaudeModal
        isOpen={setupMode}
        onClose={exitSetup}
        source="connect-claude"
        client={client}
        onSwitchStory={client.key === CONNECT_CLIENTS.mcp.key ? switchStoryInWizard : undefined}
      />

      {/* Footer — a minimal close: brand lockup + copyright, nothing more. */}
      <footer className="relative z-10 border-t border-gray-200/60 dark:border-white/[0.06] bg-white/50 dark:bg-gray-900/30 backdrop-blur-xl">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-16 py-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg p-[1.5px] bg-gradient-to-br from-blue-400 via-cyan-400 to-purple-400 shadow-md shadow-cyan-500/20">
              <div className="w-full h-full bg-white rounded-[7px] p-1 flex items-center justify-center">
                <img src="/logo-128.png" alt="Ultimate Web Scraper logo" className="w-full h-full" />
              </div>
            </div>
            <span className="text-[10px] sm:text-xs uppercase tracking-[0.25em] font-semibold text-gray-500 dark:text-gray-400">
              Ultimate Web Scraper
            </span>
          </div>
          <div className="flex flex-col sm:items-end gap-1.5">
            <p className="text-xs text-gray-400 dark:text-gray-500">
              © {new Date().getFullYear()} Ultimate Web Scraper. All rights reserved.
            </p>
            {/* Nominative-use disclaimer — the marks this story renders are
                referenced to describe compatibility, not partnership, so the
                line names whichever owners that story actually shows. */}
            <p className="text-[11px] leading-relaxed text-gray-400/80 dark:text-gray-500/80 max-w-md sm:text-right">
              {client.trademarkNote}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ConnectClaude;
